import { useEffect, useMemo } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import {
  GetCategoriesByCustomerQuery,
  GetCategoriesByCustomerQueryVariables,
  PaginatedCategories,
} from "../../../../../API";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { GET_CATEGORIES_BY_CUSTOMER } from "../../../../../common/operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../../../common/variables/common";
import { errorNotification } from "../../../../../common/variables/notification";

export const useGetAllPlateCategoriesCached = () => {
  const selectedCustomerId = useCustomerIdGuard();

  const [getCategories, { data, fetchMore, loading, networkStatus }] =
    useLazyQuery<
      GetCategoriesByCustomerQuery,
      GetCategoriesByCustomerQueryVariables
    >(GET_CATEGORIES_BY_CUSTOMER, {
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (!selectedCustomerId) return;

    getCategories({
      variables: {
        input: {
          customerId: selectedCustomerId,
          limit: DEFAULT_LIMIT_COUNT,
        },
      },
    });
  }, [selectedCustomerId, getCategories]);

  useEffect(() => {
    if (!data?.getCategoriesByCustomer?.nextToken) return;

    const fetchAllPages = async () => {
      let currentNextToken = data.getCategoriesByCustomer.nextToken;

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              input: {
                customerId: selectedCustomerId,
                limit: DEFAULT_LIMIT_COUNT,
                nextToken: currentNextToken,
              },
            },
          });

          currentNextToken =
            response.data?.getCategoriesByCustomer?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional category pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [data?.getCategoriesByCustomer?.nextToken, selectedCustomerId, fetchMore]);

  const categoriesLoading =
    loading || networkStatus === NetworkStatus.fetchMore;

  const categories = useMemo(
    () => data?.getCategoriesByCustomer as PaginatedCategories,
    [data?.getCategoriesByCustomer]
  ) as PaginatedCategories;

  return {
    categories,
    loading: categoriesLoading,
  };
};
